import React, { useState } from 'react';
import { Tweet } from '../services/tweetService';
import { demoTweets } from '../data/demoData';
import { cn } from '../lib/cn';
import { useT } from '../lib/i18n';

interface ArchiveUploadPromptProps {
  onUploadComplete: (tweets: Tweet[], isDemo?: boolean) => void;
  onSkip?: () => void;
}

export const ArchiveUploadPrompt: React.FC<ArchiveUploadPromptProps> = ({
  onUploadComplete,
  onSkip,
}) => {
  const { t } = useT();
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const parseTwitterArchive = async (file: File): Promise<Tweet[]> => {
    const text = await file.text();

    try {
      let jsonText = text;

      if (text.startsWith('window.YTD.tweets.part0 = ')) {
        jsonText = text.replace('window.YTD.tweets.part0 = ', '');
      }

      const data = JSON.parse(jsonText);

      const tweets: Tweet[] = data.map((item: any) => {
        const t = item.tweet || item;
        return {
          id: t.id_str || t.id,
          text: t.full_text || t.text || '',
          createdAt: t.created_at,
          metrics: {
            likes: parseInt(t.favorite_count || '0'),
            retweets: parseInt(t.retweet_count || '0'),
            replies: parseInt(t.reply_count || '0'),
          },
        };
      });

      return tweets.filter(t => t.text && !t.text.startsWith('RT @'));
    } catch (err) {
      throw new Error(t('archiveUpload.invalidFile'));
    }
  };

  const handleFile = async (file: File) => {
    setError(null);
    setIsProcessing(true);

    try {
      const tweets = await parseTwitterArchive(file);

      if (tweets.length < 10) {
        throw new Error(t('archiveUpload.notEnoughTweets'));
      }

      onUploadComplete(tweets);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('archiveUpload.uploadFailed'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const file = e.dataTransfer.files[0];
    if (file) {
      handleFile(file);
    }
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFile(file);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[1000] p-4">
      <div className="bg-surface-2 border border-border rounded-2xl p-6 sm:p-8 max-w-[600px] w-full animate-slide-up">
        <h2 className="text-xl sm:text-2xl font-bold text-text-primary mt-0 mb-4 tracking-tight">
          {t('archiveUpload.title')}
        </h2>

        <div
          onDrop={handleDrop}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onClick={() => document.getElementById('file-input')?.click()}
          className={cn(
            'border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-all duration-200',
            isDragging
              ? 'border-brand bg-brand-muted'
              : 'border-border hover:border-border-hover bg-surface-1'
          )}
        >
          {isProcessing ? (
            <p className="text-brand font-medium">{t('common.processing')}</p>
          ) : (
            <>
              <svg className="w-12 h-12 mx-auto mb-3 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="12" y1="18" x2="12" y2="12" /><line x1="9" y1="15" x2="15" y2="15" /></svg>
              <p
                className="text-text-primary mb-1.5"
                dangerouslySetInnerHTML={{ __html: t('archiveUpload.dragDrop') }}
              />
              <p className="text-text-muted text-sm">
                {t('archiveUpload.orClickToSelect')}
              </p>
            </>
          )}
        </div>

        <input
          id="file-input"
          type="file"
          accept=".js,.json"
          className="hidden"
          onChange={handleFileInput}
        />

        {error && (
          <div className="mt-4 text-danger text-sm">
            {error}
          </div>
        )}

        <div className="mt-5 p-4 bg-surface-1 border border-border-subtle rounded-xl text-[13px] text-text-muted">
          <p className="m-0 mb-2.5 font-semibold text-text-primary text-sm">
            {t('archiveUpload.howToGet')}
          </p>
          <ol className="m-0 pl-5 space-y-1 leading-relaxed">
            <li>
              <a
                href="https://twitter.com/settings/download_your_data"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand hover:underline"
              >
                Twitter
              </a>{' '}
              {t('archiveUpload.step1')}
            </li>
            <li>{t('archiveUpload.step2')}</li>
            <li dangerouslySetInnerHTML={{ __html: t('archiveUpload.step3') }} />
          </ol>
        </div>

        <button
          onClick={() => onUploadComplete(demoTweets, true)}
          className="mt-4 w-full py-3 bg-brand hover:bg-brand-hover text-white font-bold text-[15px] rounded-xl transition-all duration-200 shadow-glow-brand active:scale-[0.98] border-none cursor-pointer"
        >
          {t('archiveUpload.tryDemo')}
        </button>

        {onSkip && (
          <button
            onClick={onSkip}
            className="mt-2.5 w-full py-2.5 bg-transparent border border-border text-text-muted text-sm rounded-xl cursor-pointer hover:border-border-hover hover:text-text-secondary transition-all duration-150"
          >
            {t('archiveUpload.skipWithApiKey')}
          </button>
        )}
      </div>
    </div>
  );
};
